import { API_BASE_URL } from "src/config/apiBase";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Copy, Layers, Pencil, Trash2 } from "lucide-react";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie, getSessionMainId } from "../utils/getCookie";
import Loader from "../Loader";
import "./AddAdmission.css";
import "./AdmissionBatches.css";
import Swal from "sweetalert2";

/** API may return JSON array or string (MySQL JSON). */
function normalizeDays(raw) {
    if (Array.isArray(raw)) return raw.map((d) => String(d).trim()).filter(Boolean);
    if (raw == null || raw === "") return [];
    if (typeof raw === "string") {
        const s = raw.trim();
        if (!s) return [];
        try {
            const p = JSON.parse(s);
            if (Array.isArray(p)) return p.map((d) => String(d).trim()).filter(Boolean);
        } catch {
            /* fall through */
        }
        return s.split(/[,\s]+/).map((d) => d.trim()).filter(Boolean);
    }
    return [];
}

export default function AdmissionBatches() {
    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const navigate = useNavigate();
    const location = useLocation();
    const [rows, setRows] = useState([]);
    const [staffMap, setStaffMap] = useState({});
    const [load, setLoad] = useState(true);

    const headers = useMemo(
        () => ({
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }),
        [token]
    );

    const loadBatches = useCallback(async () => {
        setLoad(true);
        try {
            const mainId = getSessionMainId();
            const staffQs = mainId ? `?main_id=${encodeURIComponent(mainId)}` : "";
            const [bRes, sRes] = await Promise.all([
                fetch(`${url}/admission/batches`, { headers }),
                fetch(`${url}/staff${staffQs}`, { headers }),
            ]);
            const bJson = await bRes.json();
            const staff = sRes.ok ? await sRes.json() : [];
            const map = {};
            (Array.isArray(staff) ? staff : []).forEach((s) => {
                const id = s.emplyee_ID != null ? String(s.emplyee_ID) : "";
                if (id)
                    map[id] = [s.firstName, s.lastName].filter(Boolean).join(" ").trim() || id;
            });
            setStaffMap(map);
            setRows(Array.isArray(bJson?.batches) ? bJson.batches : []);
        } catch (e) {
            console.error(e);
            setRows([]);
        } finally {
            setLoad(false);
        }
    }, [url, headers]);

    useEffect(() => {
        loadBatches();
    }, [loadBatches, location.pathname]);

    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === "visible") loadBatches();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [loadBatches]);

    const teacherLabel = (tid) => {
        if (!tid) return "—";
        return staffMap[String(tid)] || `ID ${tid}`;
    };

    const copyBatchId = async (id) => {
        const text = String(id);
        try {
            await navigator.clipboard.writeText(text);
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "success",
                title: "Batch ID copied",
                showConfirmButton: false,
                timer: 1600,
            });
        } catch {
            Swal.fire({
                icon: "info",
                title: "Copy manually",
                text: `Batch ID: ${text}`,
            });
        }
    };

    const onDelete = async (row) => {
        const r = await Swal.fire({
            title: "Delete batch?",
            text: row.studentCount > 0 ? "Students are still assigned — delete is blocked." : "This cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
        });
        if (!r.isConfirmed) return;
        try {
            const res = await fetch(`${url}/admission/batches/${row.id}`, {
                method: "DELETE",
                headers,
            });
            const j = await res.json();
            if (!res.ok) {
                Swal.fire("Error", j.message || "Delete failed", "error");
                return;
            }
            Swal.fire("Deleted", "", "success");
            loadBatches();
        } catch (e) {
            Swal.fire("Error", e.message, "error");
        }
    };

    const isLoggedIn = getDecryptedUserFromCookie();

    return (
        <div className="admission-form-shell">
            <div className="admission-form-shell__bg" aria-hidden />
            <div className="admission-form-container admission-form-container--batches-wide">
                <header className="admission-form-header">
                    <button
                        type="button"
                        className="admission-form-back"
                        onClick={() => navigate("/admission")}
                    >
                        <ArrowLeft size={18} strokeWidth={2.25} aria-hidden />
                        Admissions
                    </button>
                    <div className="admission-form-header-text admission-batches-header__intro">
                        <h1 className="admission-batches-header__title">
                            <Layers size={22} strokeWidth={2} aria-hidden />
                            <span>Batches</span>
                        </h1>
                        <p className="admission-batches-header__subtitle">
                            Define when and where each class runs, who teaches it, and how many students it can
                            hold. New admissions pick a batch so their timetable stays in sync with the group. Use{" "}
                            <strong>Batch ID</strong> (below) in <strong>Import admissions</strong> spreadsheets — copy
                            the number or use the copy button.
                        </p>
                    </div>
                    {isLoggedIn?.main_id && (
                        <Link to="/admission-batches/new" className="admission-form-btn admission-form-btn--primary">
                            New batch
                        </Link>
                    )}
                </header>

                <div className="admission-form-card admission-batches-card">
                    <div style={{ padding: "clamp(20px, 3vw, 28px)" }}>
                        {load ? (
                            <Loader />
                        ) : rows.length === 0 ? (
                            <p className="admission-empty__hint">No batches yet. Create one to use on admissions.</p>
                        ) : (
                            <div className="admission-batches-table-wrap">
                                <table className="admission-batches-table">
                                    <thead>
                                        <tr>
                                            <th scope="col" className="admission-batches-table__th-id">
                                                Batch ID
                                            </th>
                                            <th scope="col">Name</th>
                                            <th scope="col">Center</th>
                                            <th scope="col">Teacher</th>
                                            <th scope="col">Schedule</th>
                                            <th scope="col">Days</th>
                                            <th scope="col">Students</th>
                                            <th scope="col">Status</th>
                                            <th scope="col" className="admission-batches-table__actions">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((b) => {
                                            const days = normalizeDays(b.days);
                                            const active =
                                                String(b.status || "").toLowerCase() === "active";
                                            return (
                                                <tr key={b.id}>
                                                    <td className="admission-batches-table__batch-id">
                                                        <span
                                                            className="admission-batches-table__batch-id-num"
                                                            title="Use this value in Import admissions → Batch ID column"
                                                        >
                                                            {b.id}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="admission-batches-table__copy-id"
                                                            onClick={() => copyBatchId(b.id)}
                                                            title="Copy Batch ID for Excel"
                                                            aria-label={`Copy batch ID ${b.id}`}
                                                        >
                                                            <Copy size={15} strokeWidth={2.25} aria-hidden />
                                                        </button>
                                                    </td>
                                                    <td className="admission-batches-table__name">{b.name}</td>
                                                    <td>{b.center_id}</td>
                                                    <td>{teacherLabel(b.teacher_id)}</td>
                                                    <td className="admission-batches-table__schedule">
                                                        {b.start_time} – {b.end_time}
                                                    </td>
                                                    <td className="admission-batches-table__days">
                                                        {days.length > 0 ? (
                                                            <div className="admission-batches-table__days-chips">
                                                                {days.map((d) => (
                                                                    <span
                                                                        key={d}
                                                                        className="admission-batches-table__day-chip"
                                                                    >
                                                                        {d}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="admission-batches-table__muted">
                                                                No days set
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="admission-batches-table__students">
                                                        {b.studentCount ?? 0}
                                                        {b.max_students != null && b.max_students !== ""
                                                            ? ` / ${b.max_students}`
                                                            : ""}
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={
                                                                active
                                                                    ? "admission-batches-table__status-pill admission-batches-table__status-pill--active"
                                                                    : "admission-batches-table__status-pill admission-batches-table__status-pill--inactive"
                                                            }
                                                        >
                                                            {b.status || "—"}
                                                        </span>
                                                    </td>
                                                    <td className="admission-batches-table__actions">
                                                        <div className="admission-batches-table__action-group">
                                                            <button
                                                                type="button"
                                                                className="admission-batches-icon-btn"
                                                                aria-label={`Edit batch ${b.name}`}
                                                                onClick={() =>
                                                                    navigate(`/admission-batches/${b.id}`)
                                                                }
                                                            >
                                                                <Pencil size={16} strokeWidth={2} aria-hidden />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="admission-batches-icon-btn admission-batches-icon-btn--danger"
                                                                aria-label={`Delete batch ${b.name}`}
                                                                onClick={() => onDelete(b)}
                                                            >
                                                                <Trash2 size={16} strokeWidth={2} aria-hidden />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
